import { computed, effect, inject, Injectable, signal, untracked } from '@angular/core';
import { MapService } from './map.service';
import { IndicatorsService } from './indicators.service';
import { RouteDataService } from './route-data.service';
import { CustomMarkerOptions, ItemStatus, PolyPoint, Snap } from '../interfaces/model';
import { Point, PointLocation, Transfers } from '../../../interfaces/custom-route.interface';
import { DebugService } from 'src/app/services/debug.service';
import { ScrollService } from 'src/app/services/scroll.service';
import dayjs from 'dayjs';

@Injectable({ providedIn: 'root' })
export class StepsNavigationMapService {
  private _debugService = inject(DebugService);
  private _routeDataService: RouteDataService = inject(RouteDataService);
  private _indicatorService: IndicatorsService = inject(IndicatorsService);
  private _mapService: MapService = inject(MapService);
  private _scrollService: ScrollService = inject(ScrollService)

  private isGeolocationActive = computed<boolean>(() => this._routeDataService.isGeolocationActive());
  private actualTime = computed<dayjs.Dayjs>(() => this._routeDataService.getCurrentTime())
  private currentDayIndex = computed<number>(() => this._routeDataService.currentDayIndex());
  private currentRouteDay = computed<Point[]>(() => this._routeDataService.getCurrentRouteDay());
  public currentTrameIndex = computed(() => this._routeDataService.getCurrentTrameIndex());
  public currentStepIndex = computed(() => this._routeDataService.getCurrentStepIndex());
  public activeSegmentByHour = computed(() => this._routeDataService.getActiveSegmentByHour());
  private totalTrames = computed<number>(() => this._routeDataService.getTotalTramesFromCurrentRouteDay());  
  public  totalStepsCurrentTrame = computed<number>(() => this._routeDataService.getCurrentRouteDay()?.[this.currentTrameIndex()]?.transfers?.length - 1);  
  private isToday = computed<boolean>(() => this._routeDataService.isToday());  

  private sampledPoints: google.maps.LatLngLiteral[] = [];
  public  animationRequestId: number | null = null;
  private currentIndex = 0;
  public  animationStarted = signal(false);

  private lastHeading: number | null = null;
  private canAnimate = signal(true);

  currentHourEffect = effect(() => {
    const hour: dayjs.Dayjs = this.actualTime();
    if (!this.activeSegmentByHour()) 
      return;

    this._debugService.info(hour, '[StepsNavigationMapService] currentHourEffect');
    this.getCurrentTrameByHour(hour);
  }, { allowSignalWrites: true });

  dayToShowIndexEffect = effect(() => {
    const day: number = this.currentDayIndex();     
    this._debugService.info(day, '[StepsNavigationMapService] dayToShowIndexEffect');
    untracked(() => this.loadMapByDay());
  }, { allowSignalWrites: true });

  selectActiveTrameEffect = effect(() => {
    const currentTrameIndex: number = this.currentTrameIndex(); 
    this._debugService.info(currentTrameIndex, '[StepsNavigationMapService] selectActiveTrameEffect');

    untracked(() => {
      if (this.activeSegmentByHour()) 
        this._routeDataService.setCurrentStepIndex(0);

      if (this.isGeolocationActive() || this.currentStepIndex() === undefined || currentTrameIndex === undefined) 
        return;

      this._routeDataService.setPolylineStatusOnTrameIndex(currentTrameIndex);
      this._mapService.fitCurrentTrameBounds(currentTrameIndex);

      //if (!this.isToday()){
      //  const el: HTMLElement =  document.querySelector('app-planner');
      //  this._scrollService.scrollToElementByTrameIndex(el, currentTrameIndex);
      //}
    });
  }, { allowSignalWrites: true });

  selectActiveStepEffect = effect(() => {
    const currentStepIndex: number = this.currentStepIndex();
    this._debugService.info(currentStepIndex, '[StepsNavigationMapService] selectActiveStepEffect');
    untracked(() => this.setCurrentStep());
  }, { allowSignalWrites: true });

  async loadMapByDay() {
    this.resetDay();
    if (!this.currentRouteDay()) 
      await this._routeDataService.processRouteGeometry();

    this._mapService.fitBoundsToAllPoints();

    //setTimeout(() => {
    //  if (!this.isToday()){
    //    const el: HTMLElement =  document.querySelector('app-planner');
    //    this._scrollService.scrollToElementByTrameIndex(el);
    //  }
    //}, 750);
  }

  startStepRoute() {
    if (this.currentStepIndex() === undefined || this.currentTrameIndex() === undefined) {
      this._mapService.startRoute();
      return;
    }
    setTimeout(() => this.setCurrentStep(), 50);
  }

  public nextStep(): void {  
    this.canAnimate.set(true);
    if (this.currentStepIndex() >= this.totalStepsCurrentTrame()) {
      if (this.currentTrameIndex() >= this.totalTrames() || this.activeSegmentByHour()) 
        return;

      this._indicatorService.closeAll();
      this._routeDataService.increaseCurrentTrameIndex();
      this._routeDataService.setCurrentStepIndex(0);
      return;
    }

    this._routeDataService.increaseCurrentStepIndex();
  }

  public prevStep(): void {
    this.canAnimate.set(false);
    if (this.currentStepIndex() === 0 && this.currentTrameIndex() === 0) 
      return;

    if (this.currentStepIndex() === 0) {
      if (this.activeSegmentByHour()) 
        return;
      
      this._indicatorService.closeAll();
      this._routeDataService.decreaseCurrentTrameIndex();
      const prevLastStepIndex: number = this.currentRouteDay()?.[this.currentTrameIndex()]?.transfers?.length - 1
      this._routeDataService.setCurrentStepIndex(prevLastStepIndex);
      return;
    }

    this._routeDataService.decreaseCurrentStepIndex();
  }

  startRoute() {
    this._routeDataService.setActiveSegmentByHour(false);
    this._indicatorService.resetInstructions();
    this._mapService.resetMarkerProjection();
    this._mapService.fitCurrentTrameBounds(this.currentTrameIndex());

    if (this.isGeolocationActive() || this._routeDataService.isYesterdayOrOlder(this._routeDataService.currentDate())) 
      return;

    if (!this._routeDataService.isToday()) {
      this._routeDataService.setCurrentStepIndex(0);
      this._routeDataService.setCurrentTrameIndex(0);
      return;
    }
    this._routeDataService.setActiveSegmentByHour(true);
  }

  resetDay() {
    this._routeDataService.resetCurrentStepAndTrame();
    this._mapService.resetMarkerProjection();
    this._routeDataService.resetMarkersStatus();
    this._indicatorService.resetInstructions();
  }

  public setCurrentStep() {
    if (this.currentStepIndex() === undefined || this.currentTrameIndex() === undefined) 
      return;

    this.lastHeading = null;   // evita arrastrar dirección vieja
    const goingForward = this.canAnimate(); 
    const trame = this.currentRouteDay()?.[this.currentTrameIndex()];
    const transfer = trame?.transfers?.[this.currentStepIndex()!];
    if (!trame || !transfer?.location) 
      return;

    const nextPoint: string = trame?.nextPoint;
    const prevLoc = trame.transfers?.[this.currentStepIndex()! - 1]?.location ?? trame.previousLocation;
    const currLoc = transfer.location;
    let nextLoc = null;
    for (let i = this.currentStepIndex(); i <= trame.transfers?.length; i++){
      const loc = trame.transfers?.[i]?.location;
      if (loc?.lat !== currLoc?.lat && loc?.lng !== currLoc?.lng){
        nextLoc = loc;
        break;
      }
    }

    const poly = this.buildPolyWithS(trame.polylinesPath); 
    const startSnap = this.snapOnPolyline(prevLoc, poly, goingForward, -Infinity);
    const endSnap   = this.snapOnPolyline(currLoc, poly, goingForward, startSnap.s);
    const startIdx = startSnap.segIdx + 1;        // 1º vértice **después** del proj
    const endIdx   = endSnap.segIdx + 1;          // incluye vértice final

    const clipped = [
      { lat: startSnap.point.lat(), lng: startSnap.point.lng() },
      ...poly.slice(startIdx, endIdx).map(({lat, lng}) => ({ lat, lng })),
      { lat: endSnap.point.lat(),   lng: endSnap.point.lng() },
    ];

    if (!this.isGeolocationActive()) {
      this._indicatorService.setInstructions(transfer, nextPoint);
      this.setTimeAndProgress();
    }

    if (!goingForward) {
      this.setAltMarker(currLoc, nextLoc);
      this._mapService.moveMarker(currLoc);
      return;
    }

    if (this._routeDataService.dist(prevLoc, currLoc) < 0.1)
      return;

    this.animationStarted.set(true);
    this.precomputeSampledPoints(clipped);
    this.startAnimation();
  }

  private setTimeAndProgress(): void {
    const progress: number = Math.round((this.currentStepIndex() / this.totalStepsCurrentTrame()) * 100);
    const duration: number = this.currentRouteDay()?.[this.currentTrameIndex()]?.transfers?.[this.currentStepIndex()]?.duration;
    const aproxDuration: number = Math.ceil(duration / 60);
    this._indicatorService.setTimeRemainig(aproxDuration);
    this._indicatorService.setStepProgress(progress);
  }

  private startAnimation(): void {
    this.currentIndex = 0;
    const totalFrames = this.sampledPoints.length;
    this._mapService.map.googleMap.setOptions({ gestureHandling: 'none', scrollwheel: false });

    const animate = () => {
      if (this.currentIndex >= totalFrames || !this.canAnimate()) {
        this.animationStarted.set(false);
        this.animationRequestId = null;
        this._mapService.map.googleMap.setOptions({ gestureHandling: 'greedy', scrollwheel: true });
        return;
      }

      const pos  = this.sampledPoints[this.currentIndex];
      const next = this.sampledPoints[this.currentIndex + 1] ?? (this.currentStepIndex() == 0 ? this.getNextDistinctLocation() : null);
      // const next = this.sampledPoints[this.currentIndex + 1] ?? null;
      // console.log("NEXT: 1", next)
      // console.log("NEXT: 2", test)

      if(!pos)
        return;

      this.setMarker(pos, next);
      this._mapService.moveMarker(pos);

      this.currentIndex++;
      this.animationRequestId = requestAnimationFrame(animate);
    };

    this.animationRequestId = requestAnimationFrame(animate);
  }

  private setAltMarker(position: google.maps.LatLngLiteral, nextLocation = null): void {
    const polylinesPath: PointLocation[] = this.currentRouteDay()?.[this.currentTrameIndex()]?.polylinesPath;
    const { itemLocation, itemHeading }: CustomMarkerOptions = this._routeDataService.getNearestItemInfo(position, polylinesPath, nextLocation);
    this._mapService.setMarkerAndRotation(itemLocation, itemHeading);
  }

  private setMarker(pos: google.maps.LatLngLiteral, next?: google.maps.LatLngLiteral | null): void {
    const poly = this.currentRouteDay()?.[this.currentTrameIndex()]?.polylinesPath ?? [];
    const { itemLocation } = this._routeDataService.getNearestItemInfo(pos, poly);
    let heading: number;

    if (next) {
      const d = this._routeDataService.dist(pos, next);
      if (d > 0.1) {
        heading = google.maps.geometry.spherical.computeHeading(
          new google.maps.LatLng(pos),
          new google.maps.LatLng(next)
        );
        heading = (heading + 360) % 360;        
      } 
      else {
        heading = this.lastHeading ?? 0;
      }
    } 
    else {
      heading = this.lastHeading ?? 0;
    }

    //Al comenzar la animación, evita el filtro que genera el salto:
    if (this.currentIndex <= 1 || this.lastHeading === null) {
      this.lastHeading = heading;
    } 
    else {
      // Aplica suavizado solo DESPUÉS de la primera interpolación
      const delta = ((((heading - this.lastHeading + 540) % 360) - 180));
      heading = (this.lastHeading + delta * 0.3 + 360) % 360;
      this.lastHeading = heading;
    }

    this._mapService.setMarkerAndRotation(itemLocation, heading);
  }

  private getCurrentTrameByHour(hour: dayjs.Dayjs): void {
    this.currentRouteDay()?.forEach((item, index) => {
      const start = this.currentRouteDay()?.[index - 1]?.endDate ?? dayjs().hour(8).minute(0);
      const end   = item.endDate;
      if (this._routeDataService.checkHour(start, end, hour) === ItemStatus.RUNNING) 
        this._routeDataService.setCurrentTrameIndex(index);
    });
  }

  private stripLoops(path: google.maps.LatLngLiteral[], tolM = 0.2): google.maps.LatLngLiteral[] {
    const clean: google.maps.LatLngLiteral[] = [];
    for (let i = 0; i < path.length; i++) {
      const curr = new google.maps.LatLng(path[i]);
      // ¿Ya hemos pasado (no consecutivamente) por aquí?
      const prevIdx = clean.findIndex(p => google.maps.geometry.spherical.computeDistanceBetween(new google.maps.LatLng(p), curr) <= tolM);
      const isLastPoint = i === path.length - 1;
      if (prevIdx !== -1 && !isLastPoint) {
        // Bucle detectado: recorta todo lo intermedio
        clean.splice(prevIdx + 1);
        // -NO- insertamos el punto, porque el primero ya está
        continue;
      }
      clean.push(path[i]);
    }
    return clean;
  }

  private precomputeSampledPoints(path: google.maps.LatLngLiteral[], speed = 64, fps = 60): void {
    // 1) dedup inicial sobre la ruta
    const withoutLoops = this.stripLoops(path);
    const dedup = this.dedupeConsecutive(withoutLoops, 0.2);
    const latLngs = dedup.map(p => new google.maps.LatLng(p));
    const total  = google.maps.geometry.spherical.computeLength(latLngs);
    const step   = speed / fps;
    // 2) generamos la lista completa de puntos muestreados
    const rawSamples: google.maps.LatLngLiteral[] = [];
    let acc = 0;
    let target = 0;
    let segIdx = 0;

    while (target <= total + 0.2) {
      // avanzamos segmento hasta alcanzar el target
      while (segIdx < latLngs.length - 1) {
        const A = latLngs[segIdx];
        const B = latLngs[segIdx + 1];
        const segLen = google.maps.geometry.spherical.computeDistanceBetween(A, B);
        if (acc + segLen >= target) {
          const frac = (target - acc) / segLen;
          // **Aquí** definimos P correctamente:
          const P = google.maps.geometry.spherical.interpolate(A, B, frac);
          rawSamples.push(P.toJSON());
          break;
        }
        acc += segLen;
        segIdx++;
      }
      target += step;
    }
    // añadir el último
    rawSamples.push(latLngs[latLngs.length - 1].toJSON());

    // 3) filtramos cualquier par de muestras demasiado juntas (≤10 cm)
    this.sampledPoints = rawSamples.filter((p, i, arr) => {
      if (i === 0) 
        return true;
      
      const prev = new google.maps.LatLng(arr[i - 1]);
      const curr = new google.maps.LatLng(p);
      return google.maps.geometry.spherical.computeDistanceBetween(prev, curr) > 0.1;
    });
  }

  private dedupeConsecutive(path: any[], tol = 0.2): google.maps.LatLng[] {
    if (path.length <= 1) 
      return path.slice();

    const res: google.maps.LatLng[] = [path[0]];
    for (let i = 1; i < path.length; i++) {
      const d = google.maps.geometry.spherical.computeDistanceBetween(
        new google.maps.LatLng(res[res.length - 1]), 
        new google.maps.LatLng(path[i])
      );

      if (d >= tol) 
        res.push(path[i]);
    }
    return res;
  }

  private snapOnPolyline(p: PointLocation, poly: PolyPoint[], goingFwd: boolean, sOrigin: number): Snap {
    const EPS = 1e-1; // 10 cm
    let bestDist = Infinity;
    const cand: Snap[] = [];
    for (let i = 0; i < poly.length - 1; i++) {
      const A = new google.maps.LatLng(poly[i]);
      const B = new google.maps.LatLng(poly[i + 1]);
      /* --- proyección ortogonal sobre AB ------------------------ */
      const seg   = google.maps.geometry.spherical.computeDistanceBetween(A, B);
      const ap    = google.maps.geometry.spherical.computeDistanceBetween(A, p);
      const abHd  = google.maps.geometry.spherical.computeHeading(A, B);
      const apHd  = google.maps.geometry.spherical.computeHeading(A, p);
      const projL = seg
        ? ap * Math.cos((apHd - abHd) * Math.PI / 180)
        : 0;
      const frac = Math.max(0, Math.min(1, projL / seg));
      const proj = google.maps.geometry.spherical.interpolate(A, B, frac);
      /* ----------------------------------------------------------- */
      const perp = google.maps.geometry.spherical.computeDistanceBetween(p, proj);
      if (perp < bestDist - EPS) {
        bestDist = perp;
        cand.length = 0;
      }
      if (Math.abs(perp - bestDist) < EPS) {
        const s = poly[i].s +
          google.maps.geometry.spherical.computeDistanceBetween(A, proj);
        cand.push({ s, point: proj, dist: perp, segIdx: i });
      }
    }
    /* --- elegir candidato coherente con la dirección ------------ */
    const forward = cand.filter(c => goingFwd ? c.s > sOrigin + EPS : c.s < sOrigin - EPS);
    const best = forward.length
      ? forward.reduce((m, c) => (goingFwd ? c.s < m.s : c.s > m.s) ? c : m)
      : cand[0];

    return best;
  }

  private buildPolyWithS(raw: google.maps.LatLngLiteral[]): PolyPoint[] {
    const filtered: google.maps.LatLngLiteral[] = raw.filter((p, i) =>
      i === 0 || p.lat !== raw[i - 1].lat || p.lng !== raw[i - 1].lng
    );

    const res: PolyPoint[] = [];
    let acc = 0;
    for (let i = 0; i < filtered.length; i++) {
      if (i > 0) {
        acc += google.maps.geometry.spherical.computeDistanceBetween(
          new google.maps.LatLng(filtered[i - 1]),
          new google.maps.LatLng(filtered[i])
        );
      }
      res.push({ ...filtered[i], s: acc });
    }
    return res;
  }

  private getNextDistinctLocation(): google.maps.LatLngLiteral | null {
    const currentIndex = this.currentStepIndex()
    const transfers = this.currentRouteDay()?.[this.currentTrameIndex()]?.transfers;
    const curr = transfers[this.currentStepIndex()]?.location;

    if (!curr) 
      return null;

    for (let i = currentIndex + 1; i < transfers.length; i++) {
      const candidate = transfers[i]?.location;
      if (!candidate) 
        continue;

      const d = google.maps.geometry.spherical.computeDistanceBetween(
        new google.maps.LatLng(curr),
        new google.maps.LatLng(candidate)
      );

      if (d > 1) 
        return candidate;          
    }
    return null;   
  }

}